import { useNavigate } from "react-router-dom";
import { Button, Typography } from "@/components/atoms";
import { BookingHistoryTable } from "@/components/organisms/BookingHistory";
import { YourDetailsSection } from "@/components/organisms/YourDetails";
import { PendingAssistantRequests } from "@/components/pages/Client/PendingAssistantRequests";
import { ClientScopeProvider } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGetClientProfileQuery } from "@/redux/apis/clientProfile/clientProfileApi";
import { useAppDispatch } from "@/redux/hooks";
import { clearBooking } from "@/redux/slices/clientBooking/clientBookingSlice";
import { useManageClient } from "../context";

/**
 * Operator client-record Details tab: the client's profile read-card
 * (reused `YourDetailsSection`) + booking history (reused `BookingHistoryTable`
 * pointed at the operator job endpoint via `clientId`). Both resolve the managed
 * client through `ClientScopeProvider`. The view-only banner is rendered by the
 * shell so it spans all tabs. The period stats strip is a separate story.
 */
export const ManageClientDetails = () => {
  const { clientId, isViewOnly } = useManageClient();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Deduped with YourDetailsSection's own query (same RTK key) — the label + booking eligibility.
  const { data: profile } = useGetClientProfileQuery({ accountId: clientId });

  const firstName = profile?.personal.first_name ?? "";
  const lastInitial = profile?.personal.last_name?.charAt(0);
  const clientName = [firstName, lastInitial && `${lastInitial}.`].filter(Boolean).join(" ");
  const bookLabel = clientName ? `Book a Trip for ${clientName}` : "Book a Trip";
  // Only an active account can hold a booking — mirrors ClientPickerModal's eligibility rule so a
  // pending-invite (not-yet-registered) or removed client can't be routed into the OBO sheet.
  const canBook = profile?.status === "active";

  return (
    <ClientScopeProvider accountId={clientId}>
      {/* Full-bleed gray surface, matching the Settings tab. */}
      <div className="flex flex-1 flex-col">
        <div className="flex flex-col gap-6 px-3 pb-12 pt-4 lg:px-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Typography variant="h2">Client Details</Typography>
            <Button
              variant="primary"
              size="md"
              disabled={!canBook}
              // Reset any in-flight draft so a previous attempt's locations don't carry over, then route.
              // Tag the origin so the booking confirmation's "Back" returns here (Back to Client Profile).
              onClick={() => {
                dispatch(clearBooking());
                navigate("../booking", { state: { bookingOrigin: "client-profile" } });
              }}
            >
              {bookLabel}
            </Button>
          </div>

          {/* Operator reviews/approves on the client's behalf — scoped via ClientScopeProvider. */}
          <PendingAssistantRequests />
          <YourDetailsSection readOnly={isViewOnly} />
          <BookingHistoryTable clientId={clientId} />
        </div>
      </div>
    </ClientScopeProvider>
  );
};
